import {
  chargeTypes,
  criminalOutcomeOptions,
  criminalSentenceOptions,
  offenseChargeTypes,
} from 'constants/enums/legalOptions';
import { DEFAULT_LATEST, DEFAULT_FUTURE_LATEST } from 'constants/dateLimits';
import { formIncludesPvqPartB, isPVQForm } from 'helpers/form';
import legalSentenceValidator from 'forms/sections/legal/shared/sentence';
import { hasYesOrNo } from 'models/validate';
import { ValidateJSModel } from 'forms/shared/types';
import { LegalPoliceChargeModel } from 'forms/sections/legal/police/shared/legalPoliceCharge';
import { getSafeDateTime, setToStartOfMonth } from 'helpers/date';
import { LegalPoliceOffenseItemModel } from 'forms/sections/legal/police/offense/types';
import { REQUIRED } from 'constants/errors';
import { DateTime } from 'luxon';

const chargeValidator: ValidateJSModel<LegalPoliceChargeModel> = {
  ChargeType: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: { inclusion: chargeTypes } } };
    }

    return {
      hasValue: { validator: { inclusion: offenseChargeTypes } },
      presence: true,
    };
  },
  // Validation is the same for both PVQ and non-PVQ
  CourtCharge: { presence: true, hasValue: true },
  CourtOutcome: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: { inclusion: criminalOutcomeOptions } } };
    }

    return { presence: true, hasValue: true };
  },
  CourtOutcomeOnTrialExplanation: (value, attributes, attributeName, options) => {
    if (formIncludesPvqPartB(options?.formType)
      && attributes?.CourtOutcome?.value?.includes?.('On trial')) {
      return { hasValue: true };
    }

    return {};
  },
  ChargeTypeOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.ChargeType?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },
  CourtOutcomeOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.CourtOutcome?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },
  CourtRequirementsOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.CourtOutcomeDismissedRequirements?.values?.includes?.('Other')) {
      return { hasValue: true };
    }

    return {};
  },
  CourtOutcomeDismissedRequirements: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      // Two of the values include the substring 'dismissed' as part of the string
      && attributes?.CourtOutcome?.value?.includes?.('dismissed')) {
      return {
        array: {
          length: {
            message: REQUIRED,
            minimum: 1,
          },
          validator: {
            inclusion: criminalSentenceOptions,
            presence: true,
          },
        },
        presence: true,
      };
    }

    return {};
  },
  CourtDate: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.CourtOutcome?.value?.includes?.('Awaiting trial')) {
        return {
          date: {
            earliest: getSafeDateTime(setToStartOfMonth(options.applicantBirthdate) as DateTime).datetime,
            latest: DEFAULT_FUTURE_LATEST,
            requireDay: false,
            tooEarly: 'error.applicantMin',
          },
          presence: true,
        };
      }

      return {};
    }

    const { parentAttributes } = options;
    const { Date: offenseDate } = (parentAttributes ?? {}) as LegalPoliceOffenseItemModel;

    return {
      presence: true,
      date: {
        requireDay: false, requireYear: false, earliest: offenseDate, latest: DEFAULT_LATEST, tooEarly: 'error.courtDate.min'
      },
    };
  },
  WasSentenced: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.CourtOutcome?.value?.includes('Guilty')) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  Sentence: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.CourtOutcome?.value?.includes?.('Guilty')
      && attributes.WasSentenced?.value === 'Yes') {
      return {
        model: {
          offenseDate: options.offenseDate,
          sentenceJudgmentValues: attributes.SentenceJudgements?.values,
          validator: legalSentenceValidator,
        },
        presence: true,
      };
    }

    return {};
  },
  SentenceJudgementOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.CourtOutcome?.value?.includes('Guilty')
      && attributes.WasSentenced?.value === 'Yes'
      && attributes.SentenceJudgements?.values?.includes('Other')) {
      return { hasValue: true };
    }

    return {};
  },
  SentenceJudgements: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.CourtOutcome?.value?.includes('Guilty')
      && attributes.WasSentenced?.value === 'Yes') {
      return {
        array: {
          length: {
            message: REQUIRED,
            minimum: 1,
          },
          validator: {
            inclusion: criminalSentenceOptions,
            presence: true,
          }
        },
        presence: true,
      };
    }

    return {};
  },
  SentencingDate: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.CourtOutcome?.value?.includes('Guilty')) {
      if (attributes.WasSentenced?.value === 'Yes') {
        return {
          date: {
            earliest: getSafeDateTime(setToStartOfMonth(options.applicantBirthdate) as DateTime).datetime,
            latest: DEFAULT_LATEST,
            requireDay: false,
            tooEarly: 'error.applicantMin',
          },
          presence: true,
        };
      }
      if (attributes.WasSentenced?.value === 'No') {
        return {
          date: {
            earliest: DEFAULT_LATEST,
            latest: DEFAULT_FUTURE_LATEST,
            requireDay: false,
            tooEarly: 'error.applicantMin',
          },
          presence: true,
        };
      }
    }

    return {};
  },
};

export default chargeValidator;
